import { X } from "lucide-react";
import { type ReactNode, useEffect } from "react";
import { cn } from "@/lib/cn";

/**
 * Modal es un diálogo accesible mínimo (sin dependencias extra): overlay a pantalla
 * completa con la tarjeta centrada, cierre por Escape, por el botón X o por clic en
 * el fondo (un <button> dedicado, sin manejadores en elementos estáticos), y
 * semántica role="dialog"/aria-modal. Reutilizable por los formularios y
 * confirmaciones de los maestros de fábrica (#28–#31).
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  className,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  className?: string;
}) {
  useEffect(() => {
    if (!open) {
      return;
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onClose();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
      {/* Fondo: un botón dedicado para cerrar (accesible, sin onClick en un div). */}
      <button
        type="button"
        aria-label="Cerrar"
        onClick={onClose}
        className="absolute inset-0 h-full w-full cursor-default bg-slate-900/50"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cn(
          "relative z-10 flex w-full max-w-md flex-col gap-4 rounded-t-2xl bg-white p-6 shadow-xl sm:rounded-2xl",
          className,
        )}
      >
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-bold text-slate-900 text-xl tracking-tight">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="flex h-10 w-10 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
